<template>
  <div>
    <v-toolbar flat>
      <v-subheader class="subtitle font-weight-bold text-uppercase">{{
        $t("instructions.title")
      }}</v-subheader>
      <v-spacer></v-spacer>
      <v-checkbox
        v-if="
          role('admin', 'departmentManager', 'manager', 'safetyRepresentative')
        "
        v-model="showRereading"
        :label="$t('instructions.column.review')"
      ></v-checkbox>
    </v-toolbar>

    <employee-history-instructions-table
      :instructions="
        instructions.filter(
          (i) => i.status.substring(2) === 'instructions.pending.status'
        )
      "
      :headers="headers"
      title="instructions.pending.status"
    />
    <employee-history-instructions-table
      :instructions="
        instructions.filter(
          (i) => i.status.substring(2) === 'instructions.sign.status'
        )
      "
      :headers="headers"
      title="instructions.sign.status"
    />
    <employee-history-instructions-table
      :instructions="
        instructions.filter(
          (i) => i.status.substring(2) === 'instructions.confirm.status'
        )
      "
      :headers="headers"
      title="instructions.confirm.status"
    />

    <v-subheader class="subtitle"
      >*—{{ $t("instructions.inactive") }}</v-subheader
    >
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import EmployeeHistoryInstructionsTable from "@/components/EmployeeHistory/InstructionsTable";

export default {
  props: {
    instructionsHistory: {
      type: Array,
      required: true,
    },
  },
  components: {
    EmployeeHistoryInstructionsTable,
  },
  data() {
    return {
      showRereading: false,
    };
  },
  computed: {
    ...mapGetters(["role"]),

    headers() {
      let h = [
        {
          text: this.$i18n.t("instructions.column.code"),
          value: "code",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.name"),
          value: "name",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.revision"),
          value: "version.revision",
          class: "text-uppercase subheading text-truncate",
          align: "right",
        },
        {
          text: this.$i18n.t("instructions.column.dueInDays"),
          value: "version.dueInDays",
          class: "text-uppercase subheading text-truncate",
          align: "right",
        },
        {
          text: this.$i18n.t("employees.column.name"),
          value: "signature.employee",
          class: "text-uppercase subheading text-truncate",
          align: "right",
        },
        {
          text: this.$i18n.t("instructions.column.signature"),
          value: "signature.type",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.signatureDate"),
          value: "signature.date",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.duration"),
          value: "signature.duration",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.reason"),
          value: "signature.reason",
          class: "text-uppercase subheading text-truncate",
        },
      ];

      if (this.showRereading) {
        h.push({
          text: this.$i18n.t("instructions.column.review"),
          value: "signature.rereading",
          class: "text-uppercase subheading text-truncate",
          align: "right",
        });
      }

      return h;
    },
    instructions() {
      let instructions = [];
      this.instructionsHistory &&
        this.instructionsHistory.forEach((i) => {
          let signature = i.versions[0].signatures.find(
            (s) =>
              s.type === "sign" &&
              !s.signatures.find((ss) => ss.type === "revoke")
          );

          let confirmation =
            signature &&
            signature.signatures.find(
              (s) =>
                s.type === "confirm" &&
                !s.signatures.find((ss) => ss.type === "revoke")
            );

          if (i.versions[0].markedAsRead) {
            signature = true;
            confirmation = true;
          }

          const name = i.versions[0].localizedFile.name;
          let instruction = {
            id: "i" + i.id,
            code: i.isActive ? i.code : i.code + "*",
            name,
            status: signature
              ? confirmation
                ? "3.instructions.confirm.status"
                : "2.instructions.sign.status"
              : "1.instructions.pending.status",
            isActive: i.isActive,
            isAssigned: i.isAssigned,
            signature: {
              employee: "",
              type: "",
              date: "",
              rereading: "",
            },
          };

          i.versions.forEach((v) => {
            if (v.markedAsRead) {
              v.signatures.push({
                type: "marked",
                date: v.markedAsRead.dateTime,
                employee: v.markedAsRead.whoMarked,
                signatures: [],
                reason: v.markedAsRead.reason,
              });
            }
            const ver = {
              id: instruction.id + "v" + v.id,
              revision: `${v.revision} (${this.$options.filters.date(v.date)})`,
              rereadingInDays: v.rereadingInDays,
              date: this.$options.filters.date(v.date),
              dueInDays: v.dueInDays,
              duration: v.duration,
              signatures: v.signatures,
              markedAsRead: v.markedAsRead,
            };
            if (ver.signatures && ver.signatures.length > 0) {
              this.mapSignatures(
                ver.signatures,
                ver,
                instruction,
                instructions
              );
            } else {
              Object.assign(instruction, { version: ver });
              instructions.push(Object.assign({}, instruction));
            }
          });
        });
      return instructions;
    },
  },

  methods: {
    mapSignatures(sigs, version, instruction, instructions) {
      sigs &&
        sigs
          .slice()
          .sort((a, b) => (a.date < b.date ? 1 : -1))
          .forEach((s) => {
            const sig = {
              id: "s" + s.id,
              date: s.date,
              type: `${this.$i18n.t(`profile.history.signature.${s.type}`)}`,
              reason: s.reason,
              employee: s.employee,
              rereading:
                version.rereadingInDays && s.type === "sign"
                  ? `${this.$options.filters.dateTime(
                      this.$moment(s.date)
                        .add(version.rereadingInDays, "day")
                        .format()
                    )} (${version.rereadingInDays})`
                  : version.rereadingInDays
                  ? version.rereadingInDays
                  : "",
              duration: s.type === "sign" ? version.duration : "",
              signatures: s.signatures,
            };
            let inst = instruction;
            inst.version = Object.assign({}, version);
            inst.version.id += sig.id;
            inst.signature = sig;
            instructions.push(Object.assign({}, inst));

            if (
              inst.signature.signatures &&
              inst.signature.signatures.length > 0
            ) {
              this.mapSignatures(
                sig.signatures,
                version,
                instruction,
                instructions
              );
            }
          });
    },
  },
};
</script>
